// Integrations.jsx
// Integrations tab (both roles). Contains:
//   - Teacher only: "Enable Calendar Meetings" toggle -> teacher-availability API
//   - Both: Google Calendar connect / disconnect
//
// Connection state is owned by the parent modal (so the Schedule tab and header
// can react to it); this component reports changes via onConnected/onDisconnected.

import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useAuth } from "../../features/auth/AuthContext"; // <-- adjust path
import {
  getMeetingSettings,
  updateMeetingSettings,
} from "./meetingService";
import { connectGoogleCalendar, disconnectGoogleCalendar } from "./googleCalendar";
import OneOnOneSession from "./OneOnOneSession";
import {google_calendar} from "../../assets/images/images"

// Simple Google-Calendar-style logo. Swap for your official asset if you have one.
const GCalLogo = () => (
  <svg width="30" height="30" viewBox="0 0 40 40" aria-hidden="true">
    <rect x="6" y="9" width="28" height="25" rx="4" fill="#fff" stroke="#e3e6ec" />
    <rect x="6" y="9" width="28" height="7" rx="4" fill="#4285F4" />
    <text
      x="20"
      y="30"
      textAnchor="middle"
      fontSize="13"
      fontWeight="700"
      fill="#4285F4"
      fontFamily="Arial, sans-serif"
    >
      31
    </text>
  </svg>
);

const Integrations = ({
  role,
  connected,
  account,
  available,
  onAvailabilityChange,
  onConnected,
  onDisconnected,
  showNote = true,
}) => {
  const { token } = useAuth();
  const globalState = useSelector((s) => s.globalState);
  const isTeacher = role === "Teacher";

  // Teacher's own id for the availability call. Adjust the field to match your API.
  const teacherId =
    globalState?.teacher_details?.data?.id ??
    globalState?.teacher_details?.data?.teacher_id;

  const [savingAvail, setSavingAvail] = useState(false);
  const [busy, setBusy] = useState(false); // connect / disconnect in flight
  const [savingSettings, setSavingSettings] = useState(false);
  const [error, setError] = useState("");

  // Auto-dismiss error after 3 seconds
  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(""), 3000);
    return () => clearTimeout(timer);
  }, [error]);

  const [isMeetingEnabled, setIsMeetingEnabled] = useState(false);
  const [isFreeTrial, setIsFreeTrial] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Fetch meeting settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
      const res = await getMeetingSettings(token);
      if (res.ok && res.data) {
        setIsMeetingEnabled(res.data.is_meeting_enabled ?? false);
        setIsFreeTrial(res.data.is_free_trial_available ?? false);

      const googleConnected = res.data.google_connected ?? false;
      const googleAccount = res.data.google_account;

      if (googleConnected) {
        onConnected?.(
          googleAccount?.name ||
          googleAccount?.email ||
          "Connected"
        );
      } else {
        onDisconnected?.();
      }
      } else {
        console.error("Failed to fetch settings:", res.error);
        }
      } catch (err) {
        console.error("Error fetching settings:", err);
      } finally {
        setSettingsLoaded(true);
      }
    };

    if (isTeacher && token) {
      fetchSettings();
    }
  }, [isTeacher, token]);

  // Update meeting settings via service
  const updateSettings = async (meetingEnabled, freeTrial) => {
    setSavingSettings(true);
    setError("");

    const res = await updateMeetingSettings(token, {
      is_meeting_enabled: meetingEnabled,
      is_free_trial_available: freeTrial,
    });

    if (res.ok) {
      setIsMeetingEnabled(meetingEnabled);
      setIsFreeTrial(freeTrial);
    } else {
      setError(res.error || "Failed to save settings");
    }
    setSavingSettings(false);
  };

  const toggleMeetingEnabled = () => {
    const newValue = !isMeetingEnabled;
    updateSettings(newValue, isFreeTrial);
  };

  const toggleFreeTrial = () => {
    const newValue = !isFreeTrial;
    updateSettings(isMeetingEnabled, newValue);
  };

  const toggleAvailability = async () => {
    if (savingSettings) return;
    const next = !isMeetingEnabled;
    onAvailabilityChange?.(next);

    const res = await updateMeetingSettings(token, {
      is_meeting_enabled: next,
      is_free_trial_available: isFreeTrial,
    });

    if (res.ok) {
      setIsMeetingEnabled(next);
    } else {
      onAvailabilityChange?.(!next);
      setError(res.error || "Failed to save settings");
    }
  };

  const handleConnect = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await connectGoogleCalendar(token);
      if (res.ok) {
        // Pass account name from response if available
        const accountName = res.data?.data?.google_account?.name || res.data?.data?.google_account?.email || "Connected";
        onConnected?.(accountName);

        // Auto-enable meetings if currently disabled (for teachers only)
        if (isTeacher && !isMeetingEnabled) {
          try {
            const updateRes = await updateMeetingSettings(token, {
              is_meeting_enabled: true,
              is_free_trial_available: isFreeTrial,
            });
            if (updateRes.ok) {
              setIsMeetingEnabled(true);
              onAvailabilityChange?.(true);
            }
          } catch (err) {
            console.error("Failed to auto-enable meetings:", err);
          }
        }
      } else {
        setError(res.error || "Could not connect Google Calendar.");
      }
    } catch (e) {
      // Only show error for actual failures, not for popup closure
      const isPopupClosed = /popup|closed|denied|access_denied/i.test(e?.message || "");
      if (!isPopupClosed) {
        setError(e?.message || "Could not connect Google Calendar.");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    const res = await disconnectGoogleCalendar(token);
    setBusy(false);
    if (res.ok) onDisconnected?.();
    else setError(res.error || "Could not disconnect.");
  };

  return (
    <div className="bm-integrations">

      {/* Both: Google Calendar connect / disconnect */}
      <div className="bm-integ-row">
      <div className="bm-integ-card">
        <div className="bm-integ-main">
          <span className="bm-integ-logo">
            <img src={google_calendar} alt="google_calendar"/>
          </span>
          <div className="bm-integ-info">
            <p className="bm-integ-title">Google Calendar</p>
            <p className="bm-integ-status">
              <span
                className={`bm-integ-dot ${
                  connected ? "bm-dot-on" : "bm-dot-off"
                }`}
              />
              {connected ? (account ? `Connected as ${account}` : "Connected") : "Not Connected"}
            </p>
          </div>
        </div>
        <span className="bm-integ-action">
          {connected ? (
            <button
              type="button"
              className="bm-outline-btn"
              onClick={handleDisconnect}
              disabled={busy}
            >
              {busy ? "Disconnecting…" : "Disconnect"}
            </button>
          ) : (
            <button
              type="button"
              className="bm-outline-btn"
              onClick={handleConnect}
              disabled={busy}
            >
              {busy ? "Connecting…" : "Connect"}
            </button>
          )}
        </span>
      </div>

      {error && <p className="bm-error">{error}</p>}

          {/* Teacher only: availability toggle */}
      {isTeacher && (
        <div className="bm-integ-toggle-row">
          <span className="bm-integ-label">Enable Meetings</span>
          <button
            type="button"
            className={`bm-switch ${isMeetingEnabled ? "bm-switch-on" : ""}`}
            onClick={toggleAvailability}
            disabled={savingAvail || savingSettings}
            role="switch"
            aria-checked={isMeetingEnabled}
            aria-label="Enable meetings"
          >
            <span className="bm-switch-knob" />
          </button>
        </div>
      )}

{globalState?.enabled_modules?.includes("marketplace") && isTeacher && (
        <div className="bm-free-trial flex gap-2">
          <input
            type="checkbox"
            className="bm-free-trial-box"
            checked={isFreeTrial}
            onChange={toggleFreeTrial}
            disabled={savingSettings}
            aria-label="Offer a Free Trial meeting"
          />
          <span className="bm-free-trial-text pointer-events-none">
            <span className="bm-free-trial-title text-[13px] font-semibold">
              Offer a Free Trial meeting
            </span>
            <p className="bm-free-trial-sub">
              Your first session with a student is free. Your listed price will
              apply from their second booking onward.
            </p>
          </span>
        </div>
)}
    </div>
      {/* Teacher only: 1 on 1 Session settings */}
      {isTeacher && (
        <div className="bm-oneone-section">
          {settingsLoaded ? (
          <OneOnOneSession role={role} isMeetingEnabled={isMeetingEnabled} />
          ) : (
            <div style={{ textAlign: "center", padding: "40px 0", color: "var(--bm-muted)" }}>
              Loading session settings...
            </div>
          )}
        </div>
      )}
{/* 
      {showNote && isTeacher && (
        <p className="bm-integ-note">
          <strong>Note:</strong> You can change these settings anytime from your
          profile integrations
        </p>
      )} */}
    </div>
  );
};

export default Integrations;